'use client';

import { useEffect, useMemo, useRef, useState } from 'react';
import { ChevronLeftIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import Drawer from 'components/commercetools-ui/atoms/drawer';
import LoadingImage from 'components/commercetools-ui/atoms/loading-image';
import ModalPopUp from 'components/commercetools-ui/organisms/modal-pop-up';
import useImageSizes from 'helpers/hooks/useImageSizes';
import useMediaQuery from 'helpers/hooks/useMediaQuery';
import { DEFAULT_PHOTO_HEIGHT, DEFAULT_PHOTO_WIDTH, MyPhotoItem, RemovePhotosResult } from 'helpers/hooks/useMyPhotos';
import { tablet } from 'helpers/utils/screensizes';
import { usePageLoader } from 'providers/loader';
import { interpolateCount, interpolatePhotoName } from '../../utils';
import WidgetPreviewContent from '../widget-preview-content';

type WidgetDrawerProps = {
  heading?: string;
  albumTitle?: string;
  previewHeading?: string;
  previewPhotoAriaLabel?: string;
  previousPhotoAriaLabel?: string;
  nextPhotoAriaLabel?: string;
  previewPhotoErrorMessage?: string;
  removeBtnCtaLabel?: string;
  removePhotoSuccessMessage?: string;
  removePhotoErrorMessage?: string;
  goBackBtnCtaLabel?: string;
  backBtnCtaLabel?: string;
  cancelBtnCtaLabel?: string;
  confirmModalTitle?: string;
  confirmModalDescription?: string;
  hideBtnCtaLabel?: string;
  selectedPhotosAriaLabel?: string;
  headingFontSize?: number;
  headingColor?: string;
  headingFontWeight?: 'light' | 'regular' | 'medium' | 'semibold' | 'bold';
  hideButtonBgColor?: string;
  hideButtonTextColor?: string;
  isOpen: boolean;
  photos: MyPhotoItem[];
  onHide: () => void;
  removePhoto: (photoId: string) => Promise<RemovePhotosResult>;
};

const buildColumns = (photos: MyPhotoItem[], columnCount: number) => {
  const columns: MyPhotoItem[][] = Array.from({ length: columnCount }, () => []);
  photos.forEach((photo, index) => columns[index % columnCount].push(photo));
  return columns;
};

const INITIAL_VISIBLE_COUNT = 20;
const LOAD_MORE_BATCH = 8;

const WidgetDrawer = ({
  isOpen,
  photos,
  onHide,
  removePhoto,
  heading = '{count} Photos Selected',
  albumTitle,
  previewHeading = 'Preview',
  previewPhotoAriaLabel,
  previousPhotoAriaLabel,
  nextPhotoAriaLabel,
  previewPhotoErrorMessage,
  removeBtnCtaLabel = 'Remove',
  removePhotoSuccessMessage = 'Photo removed successfully.',
  removePhotoErrorMessage = 'Unable to remove photo. Please try again.',
  backBtnCtaLabel = 'Back',
  cancelBtnCtaLabel = 'Cancel',
  confirmModalTitle = 'Remove photo?',
  confirmModalDescription = 'This photo will be removed from your cart, saved projects and wishlist.',
  hideBtnCtaLabel = 'Hide',
  selectedPhotosAriaLabel = 'Selected photos',
  headingFontSize,
  headingColor,
  headingFontWeight,
}: WidgetDrawerProps) => {
  const fontWeightByStyle = {
    light: 300,
    regular: 400,
    medium: 500,
    semibold: 600,
    bold: 700,
  } as const;

  const [isTabletSize] = useMediaQuery(tablet);
  const gridImageSizes = useImageSizes({ sm: 0.5, md: 0.34, lg: 0.34, maxWidth: 320 });
  const { showLoader, hideLoader } = usePageLoader();

  const [previewPhotoId, setPreviewPhotoId] = useState<string | null>(null);
  const [removeTargetId, setRemoveTargetId] = useState<string | null>(null);
  const [isRemoving, setIsRemoving] = useState(false);
  const [visibleCount, setVisibleCount] = useState(INITIAL_VISIBLE_COUNT);
  const [loadMoreSentinelNode, setLoadMoreSentinelNode] = useState<HTMLDivElement | null>(null);
  const [scrollContainerNode, setScrollContainerNode] = useState<HTMLDivElement | null>(null);
  const scrollPositionRef = useRef<number | null>(null);
  const previousPhotosRef = useRef<MyPhotoItem[]>(photos);

  const columnCount = isTabletSize ? 3 : 2;
  const visiblePhotos = useMemo(() => photos.slice(0, visibleCount), [photos, visibleCount]);
  const photoColumns = useMemo(() => buildColumns(visiblePhotos, columnCount), [visiblePhotos, columnCount]);
  const isPreviewing = previewPhotoId !== null;
  const hasMorePhotos = visibleCount < photos.length;

  useEffect(() => {
    const previousPhotos = previousPhotosRef.current;
    previousPhotosRef.current = photos;

    if (!previewPhotoId || photos.some((photo) => photo.id === previewPhotoId)) return;

    if (photos.length === 0) {
      setPreviewPhotoId(null);
      return;
    }

    const removedIndex = previousPhotos.findIndex((photo) => photo.id === previewPhotoId);
    const fallbackIndex = Math.min(Math.max(removedIndex, 0), photos.length - 1);
    setPreviewPhotoId(photos[fallbackIndex].id);
  }, [photos, previewPhotoId]);

  // Reset transient view state when the drawer closes so it always reopens on the
  // grid, not in a stale preview.
  useEffect(() => {
    if (isOpen) return;
    setPreviewPhotoId(null);
    setRemoveTargetId(null);
    setIsRemoving(false);
  }, [isOpen]);

  useEffect(() => {
    setVisibleCount((prev) => {
      if (photos.length === 0) return 0;
      if (prev === 0) return Math.min(INITIAL_VISIBLE_COUNT, photos.length);
      return Math.min(prev, photos.length);
    });
  }, [photos.length]);

  useEffect(() => {
    if (!previewPhotoId) return;

    const previewIndex = photos.findIndex((photo) => photo.id === previewPhotoId);
    if (previewIndex < 0) return;

    setVisibleCount((prev) => Math.max(prev, Math.min(previewIndex + 1, photos.length)));
  }, [previewPhotoId, photos]);

  useEffect(() => {
    if (isPreviewing || scrollPositionRef.current === null || !scrollContainerNode) return;

    const targetScrollTop = scrollPositionRef.current;
    scrollPositionRef.current = null;

    requestAnimationFrame(() => {
      scrollContainerNode.scrollTop = targetScrollTop;
    });
  }, [isPreviewing, scrollContainerNode]);

  useEffect(() => {
    if (isPreviewing || !hasMorePhotos || !loadMoreSentinelNode || !scrollContainerNode) return;
    if (typeof IntersectionObserver === 'undefined') return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          setVisibleCount((prev) => Math.min(prev + LOAD_MORE_BATCH, photos.length));
        });
      },
      {
        root: scrollContainerNode,
        threshold: 0.5,
      },
    );

    observer.observe(loadMoreSentinelNode);
    return () => observer.disconnect();
  }, [isPreviewing, hasMorePhotos, loadMoreSentinelNode, scrollContainerNode, photos.length]);

  const confirmRemove = async () => {
    if (!removeTargetId || isRemoving) return;
    setIsRemoving(true);
    showLoader();
    try {
      const { success } = await removePhoto(removeTargetId);
      setRemoveTargetId(null);
      if (success) {
        toast.success(removePhotoSuccessMessage);
        return;
      }

      toast.error(removePhotoErrorMessage);
    } finally {
      setIsRemoving(false);
      hideLoader();
    }
  };

  const handleDrawerClose = () => {
    if (removeTargetId) return;
    onHide();
  };

  const openPreview = (photoId: string) => {
    scrollPositionRef.current = scrollContainerNode?.scrollTop ?? 0;
    setPreviewPhotoId(photoId);
  };

  return (
    <>
      <Drawer
        isOpen={isOpen}
        direction="bottom"
        onClose={handleDrawerClose}
        role="dialog"
        aria-modal="true"
        aria-label={selectedPhotosAriaLabel}
        className="h-[90vh] w-full overflow-hidden rounded-t-[28px] bg-white"
        swipeToClose
        data-ffid="drw-my-photos-widget"
      >
        <div className="flex h-full flex-col">
          <div
            className="flex shrink-0 cursor-grab touch-none justify-center pt-10"
            data-drawer-drag-handle
            data-ffid="ctr-my-photos-widget-drawer-handle"
          >
            <div className="h-4 w-60 rounded-full bg-[#D9D9D9]" aria-hidden="true" />
          </div>

          <h2
            className="px-20 pb-14 pt-16 text-center text-24 font-regular text-[#333333]"
            style={{
              color: headingColor,
              fontSize: headingFontSize ? `${headingFontSize}px` : undefined,
              fontWeight: headingFontWeight ? fontWeightByStyle[headingFontWeight] : undefined,
            }}
            data-ffid="lbl-my-photos-widget-drawer-title"
          >
            {isPreviewing ? previewHeading : (albumTitle ?? interpolateCount(heading, photos.length))}
          </h2>

          <div className="border-t border-[#EBEBEB]" />

          {isPreviewing ? (
            <>
              <div className="flex min-h-64 shrink-0 items-center bg-[#F5F5F5] px-20">
                <button
                  type="button"
                  onClick={() => setPreviewPhotoId(null)}
                  className="flex items-center gap-4 font-medium text-[#333333]"
                  data-ffid="btn-my-photos-widget-preview-back"
                >
                  <ChevronLeftIcon className="size-16" aria-hidden="true" />
                  <span className="text-16 leading-none">{backBtnCtaLabel}</span>
                </button>
              </div>

              <div className="bg-grey-100 min-h-0 flex-1 overflow-y-auto bg-[#F5F5F5] px-16 pb-20">
                <WidgetPreviewContent
                  photos={photos}
                  activePhotoId={previewPhotoId as string}
                  onSelectPhoto={setPreviewPhotoId}
                  previewErrorMessage={previewPhotoErrorMessage}
                  previousPhotoLabel={previousPhotoAriaLabel}
                  nextPhotoLabel={nextPhotoAriaLabel}
                  enableSwipe
                />
              </div>

              <div className="flex min-h-96 shrink-0 items-center justify-center border-t border-[#D6D6D6] bg-white px-16 py-20">
                <button
                  type="button"
                  onClick={() => setRemoveTargetId(previewPhotoId)}
                  className="rounded-full border border-[#01916D] px-32 py-12 text-18 text-[#01916D]"
                  data-ffid="btn-my-photos-widget-preview-remove"
                >
                  {removeBtnCtaLabel}
                </button>
              </div>
            </>
          ) : (
            <>
              <div ref={setScrollContainerNode} className="min-h-0 flex-1 overflow-y-auto bg-[#F5F5F5] px-16 py-24">
                <div className="flex gap-12">
                  {photoColumns.map((column, columnIndex) => (
                    <div key={columnIndex} className="flex flex-1 flex-col gap-24">
                      {column.map((photo, photoIndex) => {
                        /* Photos are distributed round-robin across columns, so this recovers the grid position. */
                        const photoNumber = photoIndex * columnCount + columnIndex + 1;

                        return (
                          <div key={photo.id} className="border-b border-[#EBEBEB] pb-24">
                            <button
                              type="button"
                              onClick={() => openPreview(photo.id)}
                              className="block w-full overflow-hidden rounded-lg bg-neutral-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#018463]"
                              aria-label={interpolatePhotoName(
                                previewPhotoAriaLabel ?? 'Preview {photoName}',
                                photo.alt,
                              )}
                              data-ffid={`btn-my-photos-widget-photo-${photoNumber}`}
                            >
                              <LoadingImage
                                src={photo.gridSrc}
                                alt={photo.alt}
                                width={photo.width ?? DEFAULT_PHOTO_WIDTH}
                                height={photo.height ?? DEFAULT_PHOTO_HEIGHT}
                                sizes={gridImageSizes}
                                wrapperClassName="w-full"
                                className="h-auto w-full object-cover"
                                data-ffid={`img-my-photos-widget-grid-${photoNumber}`}
                              />
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  ))}
                </div>

                {hasMorePhotos && (
                  <div
                    ref={setLoadMoreSentinelNode}
                    className="h-1 w-full"
                    data-ffid="ctr-my-photos-widget-drawer-load-more-sentinel"
                  />
                )}
              </div>

              <div className="flex min-h-88 shrink-0 items-center justify-center border-t border-[#D6D6D6] bg-white">
                <button
                  type="button"
                  onClick={onHide}
                  className="inline-flex h-48 min-w-170 items-center justify-center rounded-full bg-[#018463] px-32 text-16 font-medium text-white"
                  data-ffid="btn-my-photos-widget-hide"
                >
                  {hideBtnCtaLabel}
                </button>
              </div>
            </>
          )}
        </div>
      </Drawer>

      <ModalPopUp
        isOpen={Boolean(removeTargetId)}
        onClose={() => setRemoveTargetId(null)}
        title={confirmModalTitle}
        messageDescription={confirmModalDescription}
        overlayZIndex={1200}
        ffidValue="my-photos-widget-remove"
        showCancelButton
        cancelText={cancelBtnCtaLabel}
        onCancel={() => setRemoveTargetId(null)}
        actionType="remove"
        actionText={removeBtnCtaLabel}
        onAction={confirmRemove}
        isActionDisabled={isRemoving}
      />
    </>
  );
};

export default WidgetDrawer;
